"use client";

import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { fetchAllDigitalAssetWithTokenByOwner } from "@metaplex-foundation/mpl-token-metadata";
import { message } from "antd";
import {
	Search,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	Badge,
} from "lucide-react";
import { Trait, DigitalAsset } from "@/app/types";
import debounce from "lodash/debounce";
import { createUmiInstance } from "@/lib/umi";
import LazyNFTMedia from "../UI/lazyLoadImage";

interface StandardNFTProps {
	selectedNFTs: DigitalAsset[];
	setSelectedNFTs: (nfts: DigitalAsset[]) => void;
}

// Custom hook for NFT data management
const useStandardNFTs = (publicKey: string | null, signer: any) => {
	const [standardNFTs, setStandardNFTs] = useState<DigitalAsset[]>([]);
	const [traits, setTraits] = useState<Trait[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	const lastFetchRef = useRef<{
		publicKey: string | null;
		signerKey: string | null;
	}>({
		publicKey: null,
		signerKey: null,
	});

	const addUniqueTraits = useCallback((newTraits: Trait[]) => {
		if (!Array.isArray(newTraits) || newTraits.length === 0) return;

		setTraits((prevTraits) => {
			const seen = new Set(
				prevTraits.map((trait) => `${trait.trait_type}_${trait.value}`),
			);
			const uniqueTraits = newTraits.filter((trait) => {
				if (!trait?.trait_type || !trait?.value) return false;
				const traitKey = `${trait.trait_type}_${trait.value}`;
				if (seen.has(traitKey)) return false;
				seen.add(traitKey);
				return true;
			});

			return [...prevTraits, ...uniqueTraits];
		});
	}, []);

	const fetchStandardNFTs = useCallback(async (force = false) => {
		message.destroy();
		if (!publicKey || !signer) return;

		// Create a stable key for the signer to avoid unnecessary refetches
		const signerKey = signer?.publicKey?.toString() || null;

		// Skip if we've already fetched for this combination and it's not forced
		if (
			!force &&
			lastFetchRef.current.publicKey === publicKey &&
			lastFetchRef.current.signerKey === signerKey
		) {
			return;
		}

		// Cancel previous request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		setIsLoading(true);
		setTraits([]);
		message.loading("Loading standard NFTs...");

		try {
			if (!signer?.publicKey) {
				throw new Error("Wallet signer not available");
			}

			// Improve type safety
			const umiSigner = {
				...signer,
				publicKey: signer.publicKey,
				signAllTransactions: async (transactions: any[]) => {
					const signedTransactions = [];
					for (const transaction of transactions) {
						if (abortController.signal.aborted) {
							throw new Error("Request aborted");
						}
						const signed = await signer.signTransaction(transaction);
						signedTransactions.push(signed);
					}
					return signedTransactions;
				},
			};

			const umi = createUmiInstance(umiSigner);
			const standardNfts = await fetchAllDigitalAssetWithTokenByOwner(
				umi,
				publicKey as any,
			);

			if (abortController.signal.aborted) return;

			// Batch metadata fetching with error handling
			const processedStandardNFTs = await Promise.allSettled(
				standardNfts.map(async (nft) => {
					if (abortController.signal.aborted) {
						throw new Error("Request aborted");
					}

					try {
						const response = await fetch(nft.metadata.uri, {
							signal: abortController.signal,
						});
						if (!response.ok) throw new Error("Failed to fetch metadata");
						const metadata = await response.json();

						return {
							...nft,
							isCompressed: false,
							metadata_uri: {
								...metadata,
								publicKey: nft.publicKey,
								owner: publicKey,
								mint: nft.mint.publicKey,
								updateAuthority: nft.metadata.updateAuthority,
							},
						} as DigitalAsset;
					} catch (error) {
						console.error(`Failed to fetch metadata for NFT: ${error}`);
						return null;
					}
				}),
			);

			if (abortController.signal.aborted) return;

			// Filter successful results
			const validStandardNFTs = processedStandardNFTs
				.filter(
					(result): result is PromiseFulfilledResult<DigitalAsset | null> =>
						result.status === "fulfilled",
				)
				.map((result) => result.value)
				.filter(
					(nft): nft is DigitalAsset =>
						nft !== null &&
						!!nft.metadata_uri?.image &&
						!!nft.metadata_uri?.name,
				);

			setStandardNFTs(validStandardNFTs);
			message.destroy();
			message.success(`Loaded ${validStandardNFTs.length} standard NFTs`);

			// Extract and add traits
			const allTraits = validStandardNFTs.flatMap((nft: DigitalAsset) => {
				const attributes = nft.metadata_uri?.attributes;
				return Array.isArray(attributes)
					? attributes.filter(
							(trait): trait is Trait =>
								trait &&
								typeof trait.trait_type === "string" &&
								typeof trait.value === "string" &&
								trait.trait_type.trim() !== "" &&
								trait.value.trim() !== "",
						)
					: [];
			});

			addUniqueTraits(allTraits);

			// Update the last fetch reference
			lastFetchRef.current = { publicKey, signerKey };
		} catch (error) {
			if (!abortController.signal.aborted) {
				console.error("Failed to fetch standard NFTs:", error);
				message.error("Failed to fetch standard NFTs. Please try again.");
			}
		} finally {
			if (!abortController.signal.aborted) {
				setIsLoading(false);
				message.destroy();
			}
		}
	}, [publicKey, signer, addUniqueTraits]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			message.destroy();
		};
	}, []);

	return {
		standardNFTs,
		traits,
		isLoading,
		fetchStandardNFTs,
	};
};

// Custom hook for filtering and pagination
const useNFTFiltering = (
	standardNFTs: DigitalAsset[],
	selectedTraits: Trait[],
	itemsPerPage: number = 20,
) => {
	const [currentPage, setCurrentPage] = useState(1);

	const filteredNFTs = useMemo(() => {
		if (selectedTraits.length === 0) return standardNFTs;

		return standardNFTs.filter((nft) => {
			const nftAttributes = nft.metadata_uri?.attributes;
			if (!Array.isArray(nftAttributes)) return false;

			return selectedTraits.every((selectedTrait) =>
				nftAttributes.some(
					(nftTrait) =>
						nftTrait?.trait_type?.toLowerCase() ===
							selectedTrait.trait_type?.toLowerCase() &&
						nftTrait?.value?.toLowerCase() ===
							selectedTrait.value?.toLowerCase(),
				),
			);
		});
	}, [standardNFTs, selectedTraits]);

	const paginatedNFTs = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		return filteredNFTs.slice(startIndex, startIndex + itemsPerPage);
	}, [filteredNFTs, currentPage, itemsPerPage]);

	const totalPages = Math.ceil(filteredNFTs.length / itemsPerPage);

	const handlePageChange = useCallback(
		(newPage: number) => {
			if (newPage >= 1 && newPage <= totalPages) {
				setCurrentPage(newPage);
				window.scrollTo({ top: 0, behavior: "smooth" });
			}
		},
		[totalPages],
	);

	// Reset page when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedTraits, filteredNFTs.length]);

	return {
		filteredNFTs,
		paginatedNFTs,
		currentPage,
		totalPages,
		handlePageChange,
	};
};

const StandardNFT = ({ selectedNFTs, setSelectedNFTs }: StandardNFTProps) => {
	const { signer, publicKey } = usePrivyWallet();
	const [selectedTraits, setSelectedTraits] = useState<Trait[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isHydrated, setIsHydrated] = useState(false);

	// Prevent hydration mismatch
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	// Use custom hooks
	const { standardNFTs, traits, isLoading, fetchStandardNFTs } =
		useStandardNFTs(publicKey, signer);

	const {
		filteredNFTs,
		paginatedNFTs,
		currentPage,
		totalPages,
		handlePageChange,
	} = useNFTFiltering(standardNFTs, selectedTraits);

	// Ref for cleanup
	const debouncedSearchRef = useRef<any>(null);

	// Initialize debounced search with cleanup
	useEffect(() => {
		debouncedSearchRef.current = debounce((term: string) => {
			setSearchTerm(term);
		}, 300);

		return () => {
			if (debouncedSearchRef.current) {
				debouncedSearchRef.current.cancel();
			}
		};
	}, []);

	// Initial fetch - only when publicKey or signer change meaningfully
	useEffect(() => {
		if (publicKey && signer?.publicKey) {
			fetchStandardNFTs();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [publicKey, signer?.publicKey]); // Only depend on stable identifiers

	// Filter traits based on search term
	const filteredTraits = useMemo(() => {
		if (!searchTerm.trim()) return traits;

		const searchLower = searchTerm.toLowerCase().trim();
		return traits.filter(
			(trait) =>
				trait.value.toLowerCase().includes(searchLower) ||
				trait.trait_type.toLowerCase().includes(searchLower),
		);
	}, [traits, searchTerm]);

	const selectAllFilteredNFTs = useCallback(() => {
		const standardFilteredNFTs = filteredNFTs.filter(
			(nft) => !nft.isCompressed,
		);

		const hasCompressedNFTs = selectedNFTs.some(
			(selected) => selected.isCompressed,
		);

		if (hasCompressedNFTs) {
			message.error(
				"You cannot mix standard and compressed NFTs. Please clear your selection first.",
			);
			return;
		}

		const allFilteredSelected = standardFilteredNFTs.every((nft) =>
			selectedNFTs.some(
				(selected) =>
					(selected?.publicKey || selected?.mint?.publicKey || selected?.mint) ===
					(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
			),
		);

		if (allFilteredSelected) {
			setSelectedNFTs(
				selectedNFTs.filter(
					(nft) =>
						!standardFilteredNFTs.some(
							(filtered) =>
								(filtered?.publicKey ||
									filtered?.mint?.publicKey ||
									filtered?.mint) ===
								(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
						),
				),
			);
		} else {
			const newSelection = new Set([...selectedNFTs, ...standardFilteredNFTs]);
			setSelectedNFTs(Array.from(newSelection));
		}
	}, [filteredNFTs, selectedNFTs, setSelectedNFTs]);

	const handleTraitSelection = useCallback((trait: Trait) => {
		if (!trait?.trait_type || !trait?.value) return;

		setSelectedTraits((prev) => {
			const isSelected = prev.some(
				(t) =>
					t.trait_type?.toLowerCase() === trait.trait_type?.toLowerCase() &&
					t.value?.toLowerCase() === trait.value?.toLowerCase(),
			);

			return isSelected
				? prev.filter(
						(t) =>
							!(
								t.trait_type?.toLowerCase() ===
									trait.trait_type?.toLowerCase() &&
								t.value?.toLowerCase() === trait.value?.toLowerCase()
							),
					)
				: [...prev, trait];
		});
	}, []);

	const toggleNftSelection = useCallback(
		(nft: DigitalAsset) => {
			if (!nft || nft.isCompressed) {
				if (nft?.isCompressed) {
					message.error(
						"This is a compressed NFT. Please use the Compressed NFTs tab to select it.",
					);
				}
				return;
			}

			const hasCompressedNFTs = selectedNFTs.some(
				(selected) => selected.isCompressed,
			);

			if (hasCompressedNFTs) {
				message.error(
					"You cannot mix standard and compressed NFTs. Please clear your selection first.",
				);
				return;
			}

			setSelectedNFTs(
				selectedNFTs.some(
					(selected) =>
						(selected?.publicKey || selected?.mint?.publicKey || selected?.mint) ===
						(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
				)
					? selectedNFTs.filter(
							(selected) =>
								(selected?.publicKey ||
									selected?.mint?.publicKey ||
									selected?.mint) !==
								(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
					  )
					: [...selectedNFTs, nft],
			);
		},
		[selectedNFTs, setSelectedNFTs],
	);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (debouncedSearchRef.current) {
				debouncedSearchRef.current(e.target.value);
			}
		},
		[],
	);

	// Computed values
	const standardNFTCount = standardNFTs.length;
	const selectedStandardCount = selectedNFTs.filter(
		(nft) => !nft.isCompressed,
	).length;

	// Show loading state during hydration
	if (!isHydrated) {
		return (
			<div className="text-center py-12">
				<div className="animate-spin size-8 border-4 border-mgt-green border-t-transparent rounded-full mx-auto mb-4" />
				<p className="text-gray-400">Loading...</p>
			</div>
		);
	}

	return (
		<div>
			<div className="mb-8">
				<div className="flex items-center justify-between mb-6">
					<h2 className="text-xl font-bold text-white">
						Standard NFTs{" "}
						<span className="text-mgt-green">({standardNFTCount})</span>
					</h2>
					<span className="text-white text-sm">
						{selectedStandardCount} selected
					</span>
				</div>

				{/* Search and filters */}
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<input
							type="text"
							placeholder="Search traits..."
							onChange={handleSearchChange}
							className="w-full bg-[#1A1A1A] text-white px-4 py-3 pl-12 rounded-xl border-none focus:ring-2 focus:ring-mgt-green outline-none"
							aria-label="Search traits"
						/>
						<Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 size-5" />
					</div>

					<div className="flex-1">
						<div className="flex flex-wrap gap-2">
							{filteredTraits.map((trait, index) => (
								<button
									key={`${trait.trait_type}-${trait.value}-${index}`}
									onClick={() => handleTraitSelection(trait)}
									className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${
											selectedTraits.some(
												(t) =>
													t.trait_type?.toLowerCase() ===
														trait.trait_type?.toLowerCase() &&
													t.value?.toLowerCase() ===
														trait.value?.toLowerCase(),
											)
												? "bg-mgt-green text-black"
												: "bg-[#1A1A1A] text-white hover:bg-[#2A2A2A]"
										}`}
								>
									{trait.value}
								</button>
							))}
						</div>
					</div>
				</div>
			</div>

			{filteredNFTs.length > 0 && (
				<div className="bg-[#1A1A1A] rounded-xl p-4 mb-6">
					<div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
						<div className="flex items-center gap-4 w-full sm:w-auto">
							<button
								onClick={selectAllFilteredNFTs}
								className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors 
                  ${
										filteredNFTs.every((nft) =>
											selectedNFTs.some(
												(selected) =>
													(selected?.publicKey ||
														selected?.mint?.publicKey ||
														selected?.mint) ===
													(nft?.publicKey ||
														nft?.mint?.publicKey ||
														nft?.mint),
											),
										)
											? "bg-[#2A2A2A] text-white hover:bg-[#3A3A3A] border border-gray-600"
											: "bg-mgt-green bg-opacity-10 text-mgt-green hover:bg-opacity-20 border border-mgt-green"
									}`}
							>
								{filteredNFTs.every((nft) =>
									selectedNFTs.some(
										(selected) =>
											(selected?.publicKey ||
												selected?.mint?.publicKey ||
												selected?.mint) ===
											(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
									),
								)
									? "Deselect All"
									: "Select All Filtered"}
							</button>
						</div>
					</div>
				</div>
			)}

			{isLoading ? (
				<div className="text-center py-12">
					<div className="animate-spin size-8 border-4 border-mgt-green border-t-transparent rounded-full mx-auto mb-4" />
					<p className="text-gray-400">Loading standard NFTs...</p>
				</div>
			) : filteredNFTs.length > 0 ? (
				<>
					<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{paginatedNFTs.map((nft, index) => {
							if (
								!nft?.publicKey ||
								!nft?.metadata_uri?.image ||
								!nft?.metadata_uri?.name
							) {
								return null;
							}

							const isSelected = selectedNFTs.some(
								(selected) =>
									(selected?.publicKey ||
										selected?.mint?.publicKey ||
										selected?.mint) ===
									(nft?.publicKey || nft?.mint?.publicKey || nft?.mint),
							);

							return (
								<div
									key={nft.publicKey}
									onClick={() => toggleNftSelection(nft)}
									className={`bg-[#1A1A1A] rounded-xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] relative
                    ${
											isSelected
												? "ring-2 ring-mgt-green"
												: "hover:ring-1 hover:ring-gray-500"
										}`}
									role="button"
									tabIndex={0}
									aria-pressed={isSelected}
									aria-label={`Select ${nft.metadata_uri.name}`}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											toggleNftSelection(nft);
										}
									}}
								>
									<div className="aspect-square relative">
										<LazyNFTMedia
											src={nft.metadata_uri.image}
											alt={nft.metadata_uri.name}
											isSelected={isSelected}
											isCompressed={false}
											width={300}
											height={300}
											quality={85}
											loading={index < 8 ? "eager" : "lazy"}
											className="w-full h-full"
										/>
										{isSelected && (
											<div className="absolute top-2 right-2">
												<CheckCircle2 className="size-6 text-mgt-green" />
											</div>
										)}
									</div>
									<div className="p-3">
										<h3 className="text-sm font-medium text-white truncate">
											{nft.metadata_uri.name}
										</h3>
									</div>
								</div>
							);
						})}
					</div>

					{totalPages > 1 && (
						<div className="flex justify-center items-center gap-4 mt-8 mb-8">
							<button
								onClick={() => handlePageChange(currentPage - 1)}
								disabled={currentPage === 1}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Previous page"
							>
								<ChevronLeft className="size-5" />
							</button>

							<span className="text-white">
								Page {currentPage} of {totalPages}
							</span>

							<button
								onClick={() => handlePageChange(currentPage + 1)}
								disabled={currentPage === totalPages}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Next page"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>
					)}
				</>
			) : (
				<div className="text-center py-12 bg-[#1A1A1A] rounded-xl p-10">
					<Badge className="size-12 text-gray-500 mx-auto mb-4" />
					<h3 className="text-xl font-semibold text-white mb-2">
						No Standard NFTs Found
					</h3>
					<p className="text-gray-400">
						{selectedTraits.length > 0
							? "Try adjusting your trait filters"
							: "No standard NFTs found in your wallet"}
					</p>
					{publicKey && (
						<button
							className="my-5 bg-mgt-green rounded-xl py-3 px-2 text-black hover:bg-opacity-90 transition-opacity disabled:opacity-50 w-full"
							onClick={() => fetchStandardNFTs(true)}
							disabled={isLoading}
						>
							{isLoading ? "Loading..." : "Refresh NFTs"}
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default StandardNFT;
